import { z } from 'zod';
import { ACCOUNT_NUMBER_REGEX, getSshPublicKeyError, isScientificNotation, isValidRoutingNumberChecksum, MAX_SSH_PUBLIC_KEYS, MAX_SSH_PUBLIC_KEYS_MESSAGE, normalizeSshPublicKeys, PHONE_NUMBER_REGEX, ROUTING_NUMBER_REGEX, } from '../utils/index.js';
export var UserRole;
(function (UserRole) {
    UserRole["USER"] = "user";
    UserRole["ADMIN"] = "admin";
    UserRole["SUPER_ADMIN"] = "super_admin";
})(UserRole || (UserRole = {}));
// Zod Schemas for DTOs
export const LoginCredentialsSchema = z.object({
    email: z.string().email('Invalid email format'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
});
export const RegisterDataSchema = z.object({
    email: z.string().email('Invalid email format'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
});
export const UpdateUserSchema = z.object({
    firstName: z.string().min(1).optional(),
    lastName: z.string().min(1).optional(),
    email: z.string().email().optional(),
});
export const FileUploadSchema = z.object({
    originalName: z.string(),
    mimeType: z.string(),
    size: z.number().positive(),
});
export var NotificationLevel;
(function (NotificationLevel) {
    NotificationLevel["INFO"] = "info";
    NotificationLevel["SUCCESS"] = "success";
    NotificationLevel["WARNING"] = "warning";
    NotificationLevel["ERROR"] = "error";
})(NotificationLevel || (NotificationLevel = {}));
export const CreateNotificationSchema = z.object({
    level: z.nativeEnum(NotificationLevel),
    title: z.string().min(1),
    message: z.string().min(1),
    userId: z.string().uuid(),
    data: z.record(z.unknown()).optional(),
});
// ==================== ROUTING NUMBER VALIDATION SCHEMAS ====================
/**
 * Zod schema for routing number validation (required field)
 * Validates that the routing number is exactly 9 digits and passes ABA checksum.
 * Allows leading zeros (e.g., "000123456" is valid if checksum passes).
 *
 * @example
 * const schema = z.object({ routingNumber: RoutingNumberSchema });
 * schema.parse({ routingNumber: '021000021' }); // OK (valid checksum)
 * schema.parse({ routingNumber: '123456789' }); // Error: invalid checksum
 * schema.parse({ routingNumber: '12345678' }); // Error: must be exactly 9 digits
 */
export const RoutingNumberSchema = z
    .string()
    .min(1, 'Routing number is required')
    .regex(ROUTING_NUMBER_REGEX, 'Routing number must be exactly 9 digits')
    .refine((val) => isValidRoutingNumberChecksum(val), {
    message: 'Routing number is invalid',
});
/**
 * Zod schema for optional routing number validation
 * When provided, validates that the routing number is exactly 9 digits and passes ABA checksum.
 * Allows null, undefined, or empty string.
 */
export const OptionalRoutingNumberSchema = z
    .string()
    .regex(ROUTING_NUMBER_REGEX, 'Routing number must be exactly 9 digits')
    .refine((val) => isValidRoutingNumberChecksum(val), {
    message: 'Routing number is invalid',
})
    .nullable()
    .optional()
    .or(z.literal(''));
/**
 * Zod schema for routing number that allows null/undefined but validates when present
 * When provided, validates format and ABA checksum.
 */
export const NullableRoutingNumberSchema = z
    .string()
    .regex(ROUTING_NUMBER_REGEX, 'Routing number must be exactly 9 digits')
    .refine((val) => isValidRoutingNumberChecksum(val), {
    message: 'Routing number is invalid',
})
    .nullable()
    .optional();
// ==================== ACCOUNT NUMBER VALIDATION SCHEMAS ====================
/**
 * Zod schema for account number validation (required field)
 * Validates that the account number is 4-17 digits and not in scientific notation.
 *
 * @example
 * const schema = z.object({ accountNumber: AccountNumberSchema });
 * schema.parse({ accountNumber: '1234567890' }); // OK
 * schema.parse({ accountNumber: '1234' }); // OK (minimum 4 digits)
 * schema.parse({ accountNumber: '123' }); // Error: Account number must be 4-17 digits
 * schema.parse({ accountNumber: '1.23e+10' }); // Error: scientific notation
 */
export const AccountNumberSchema = z
    .string()
    .min(1, 'Account number is required')
    .regex(ACCOUNT_NUMBER_REGEX, 'Account number must be 4-17 digits')
    .refine((val) => !isScientificNotation(val), {
    message: 'Account number cannot be in scientific notation format (e.g., 1.23e+10)',
});
/**
 * Zod schema for optional account number validation
 * When provided, validates that the account number is 4-17 digits
 * and not in scientific notation.
 * Allows null, undefined, or empty string.
 */
export const OptionalAccountNumberSchema = z
    .string()
    .regex(ACCOUNT_NUMBER_REGEX, 'Account number must be 4-17 digits')
    .refine((val) => !isScientificNotation(val), {
    message: 'Account number cannot be in scientific notation format (e.g., 1.23e+10)',
})
    .nullable()
    .optional()
    .or(z.literal(''));
/**
 * Zod schema for account number that allows null/undefined but validates when present
 * When provided, validates format and scientific notation.
 */
export const NullableAccountNumberSchema = z
    .string()
    .regex(ACCOUNT_NUMBER_REGEX, 'Account number must be 4-17 digits')
    .refine((val) => !isScientificNotation(val), {
    message: 'Account number cannot be in scientific notation format (e.g., 1.23e+10)',
})
    .nullable()
    .optional();
// ==================== PHONE NUMBER VALIDATION SCHEMAS ====================
/**
 * Zod schema for phone number validation (required field).
 * E.164-compatible: optional leading +, starts with non-zero digit, 2-15 digits total.
 */
export const PhoneNumberSchema = z
    .string()
    .min(1, 'Phone number is required')
    .regex(PHONE_NUMBER_REGEX, 'Invalid phone number format');
/**
 * Zod schema for optional phone number validation.
 * When provided, validates E.164 format. Allows null, undefined, or empty string.
 */
export const OptionalPhoneNumberSchema = z
    .string()
    .regex(PHONE_NUMBER_REGEX, 'Invalid phone number format')
    .nullable()
    .optional()
    .or(z.literal(''));
/**
 * Zod schema for phone number that allows null/undefined but validates when present.
 */
export const NullablePhoneNumberSchema = z
    .string()
    .regex(PHONE_NUMBER_REGEX, 'Invalid phone number format')
    .nullable()
    .optional();
// ==================== SSH PUBLIC KEY VALIDATION SCHEMAS ====================
/**
 * Zod schema for a user's full set of SSH public keys.
 *
 * Entries are normalized before validation: newline-separated entries are split
 * apart, each entry is trimmed, blanks are dropped, and duplicates are removed
 * (comparing type and key body only, so the same key with a different comment
 * is still a duplicate). An empty array is valid and means "no keys".
 *
 * Invalid entries are reported with their 1-based position so the message stays
 * useful once the API flattens validation errors into a single string.
 *
 * The count cap is applied *after* normalization, because normalization splits
 * newline-separated values apart and can therefore increase the count — a single
 * submitted entry holding hundreds of keys would slip past a check on the raw
 * array. Bounding it keeps an unreasonably large multivalued attribute out of
 * Keycloak, which the SFTP IdP Lambda re-reads on every login.
 */
export const SshPublicKeyListSchema = z
    .array(z.string())
    .transform(normalizeSshPublicKeys)
    .superRefine((keys, ctx) => {
    if (keys.length > MAX_SSH_PUBLIC_KEYS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${MAX_SSH_PUBLIC_KEYS_MESSAGE} (received ${keys.length})`,
        });
        // Stop here rather than also emitting one error per entry.
        return;
    }
    keys.forEach((key, index) => {
        const message = getSshPublicKeyError(key);
        if (message) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `SSH public key ${index + 1}: ${message}`,
                path: [index],
            });
        }
    });
});
/**
 * Zod schema for a set of SSH public keys that allows null/undefined but
 * validates when present. Omitting the field leaves stored keys untouched;
 * passing an empty array or null clears them.
 */
export const NullableSshPublicKeyListSchema = SshPublicKeyListSchema.nullable().optional();
// Effective access level
export * from './access-level.enum.js';
// BAI2 types
export * from './bai2.types.js';
// Email template types
export * from './email-templates.js';
// User type enum
export * from './user-type.enum.js';
//# sourceMappingURL=index.js.map